import { sanitizeURL } from '../../shared/utils/sanitize';
import { Nullable } from '../types';
import type { HandoffLeadDetails } from './api';
import {
  closeTextHandoff,
  fetchChatResponse,
  fetchTextHandoffStatus,
  notifyTextHandoffPresence,
  requestTextHandoff,
  resolveApiBaseUrl,
} from './api';
import {
  appendInlineCalendlyWidget,
  collapseWidget,
  detectBookingIntent,
  restoreChatMessages,
  showCalendlyLoadingMessage,
} from './booking-helpers';
import {
  CalendlyEventData,
  fetchWidgetConfig,
  initCalendlyInline,
  loadCalendlyScript,
  logMeetingBooking,
} from './calendly';
import {
  appendHandoffNotification,
  getPrimaryMessageWrapper,
  recieveAndAppendMessage,
  sendAndAppendMessage,
  showDots,
} from './message-helpers';
import { templateTextWidget } from './template';
import {
  clearHandoffId,
  createToken,
  getActiveHandoffSessionToken,
  getStoredHandoffId,
  resetToken,
  storeActiveHandoffSession,
} from './token';
import { MainWindow, TextChatUIElements } from './types';

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'yandex.com',
  'rediffmail.com',
]);

const LANGUAGE_LABELS: Record<string, string> = {
  de: 'German',
  deu: 'German',
  german: 'German',
  fr: 'French',
  fre: 'French',
  fra: 'French',
  fe: 'French',
  french: 'French',
  en: 'English',
  eng: 'English',
  english: 'English',
  es: 'Spanish',
  spa: 'Spanish',
  spanish: 'Spanish',
};

class WitzoChatElement extends HTMLElement {
  /**Url to connect to the webhook */
  url: Nullable<string> = null;
  /**Reference for elements */
  chat: Nullable<TextChatUIElements> = null;

  default_primary_text: Nullable<string> = null;

  bot_logo_color: Nullable<string> = ``;

  send_button_color: Nullable<string> = ``;

  unique_id: Nullable<string> = ``;
  apiBaseUrl: Nullable<string> = null;
  private responseLanguage: string = '';

  user_chat_color: Nullable<string> = '';

  private readonly handoffPresenceHandler = () => {
    void this.markHandoffVisitorLeft('page_unload', true);
  };
  private readonly handoffVisibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      void this.markHandoffVisitorLeft('page_hidden', true);
    } else {
      void this.markHandoffVisitorActive('page_visible');
    }
  };
  private readonly handoffOnlineHandler = () => {
    void this.markHandoffVisitorActive('network_online');
  };
  private readonly handoffOfflineHandler = () => {
    void this.markHandoffVisitorLeft('network_offline', true);
  };
  private readonly handoffActivityHandler = () => {
    if (!this.handoffStatus) return;
    this.resetHandoffInactivityTimer();
    if (this.handoffMarkedLeft && document.visibilityState !== 'hidden') {
      void this.markHandoffVisitorActive('user_activity');
    }
  };
  private readonly activeHandoffSessionHandler = () => {
    const activeSession = getActiveHandoffSessionToken();
    if (!activeSession || activeSession === this.sessionId || this.handoffStatus) return;
    this.sessionId = activeSession;
    void this.restoreExistingHandoff();
  };

  private sessionId: string = '';
  private latestRequestId: number = 0;

  private calendlyUrl: Nullable<string> = null;
  private calendlyInitialized: boolean = false;
  private calendlyListenerAttached: boolean = false;
  private loggedEventUris: Set<string> = new Set();

  private feedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffPollTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private handoffInactiveTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffEventSource: EventSource | null = null;
  private handoffWaitingWrapper: HTMLDivElement | null = null;
  private handoffWaitingTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffAwaitingAgentReply: boolean = false;
  private handoffRestoring: boolean = false;
  private feedbackSubmitted: boolean = false;
  private static FEEDBACK_IDLE_MS = 45_000; // 45 seconds
  private handoffEnabled: boolean = false;
  private handoffStatus: 'waiting_for_agent' | 'assigned' | 'closed' | null = null;
  private handoffAssignedNotified: boolean = false;
  private handoffMarkedLeft: boolean = false;
  private seenHandoffMessageIds: Set<string> = new Set();
  private handoffReconnectAvailableAt: number = 0;
  private handoffLeadFormWrapper: HTMLDivElement | null = null;
  /** Slow poll used only while watching for an agent takeover (SSE fallback). */
  private handoffWatchTimer: ReturnType<typeof setTimeout> | null = null;
  /** Gates the takeover watch: no chat opened means nothing to take over. */
  private hasOpenedWidget: boolean = false;
  private static HANDOFF_POLL_MS = 1500;
  // Deliberately slow: this runs for every visitor with no handoff in progress,
  // and only needs to catch an agent takeover, which is rare. SSE does the
  // real-time work; this is just the fallback when EventSource is unavailable.
  private static HANDOFF_WATCH_POLL_MS = 20_000;
  private static HANDOFF_HEARTBEAT_MS = 30_000;
  private static HANDOFF_INACTIVE_MS = 180_000;
  private static HANDOFF_RECONNECT_COOLDOWN_MS = 10_000;

  /**Is Initialized */
  isInitialized: boolean = false;
  private _mainWindow: MainWindow;

  private succesfulChatCount: number;
  private date: Date;
  private defaultMessageDisplayed: boolean = false;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(node);

    this._mainWindow = {
      back: () => {},
      close: () => {},
    };

    this.getElements(root);
    this.succesfulChatCount = 0;
    this.date = new Date();

    const date = sessionStorage.getItem('witzo_chat_date');
    if (date) {
      this.date = new Date(date);
    } else {
      sessionStorage.setItem('witzo_chat_date', new Date().toISOString());
    }

    const chatCount = sessionStorage.getItem('witzo_chat_count');
    if (chatCount) this.succesfulChatCount = Number(chatCount);
    else sessionStorage.setItem('witzo_chat_count', `0`);

    // Initialize as null instead of empty string
    this.default_primary_text = null;
    this.bot_logo_color = ``;
    this.send_button_color = '';
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selctor: string) => root.querySelector(selctor) as T;
    try {
      this.chat = {
        backButton: query('#backToSelection'),
        chatWindow: query('#textChatWidget'),
        closeButton: query('#closeTextChat'),
        sendButton: query('#textSendButton'),
        messageInput: query('#textMessageInput'),
        messagesContainer: query('#textMessagesArea'),
        sendButtonRect: root.querySelector('#send-button-color') as SVGRectElement,
        bannerText: query('#banner-text') as HTMLHeadingElement,
        bannerTextParagraph: query('#banner-text-paragraph') as HTMLParagraphElement,
        logoIcon: query('#logoIcon') as HTMLImageElement,
        closeIcon: query('#close-button-icon') as HTMLElement,
        chatHeader: query('#chat-header') as HTMLDivElement,
        calendlyButton: query('#calendlyButton') as HTMLButtonElement,
        calendlyButtonWrap: query('#calendlyButtonWrap') as HTMLDivElement,
        handoffButton: query('#handoffButton') as HTMLButtonElement,
        handoffButtonWrap: query('#handoffButtonWrap') as HTMLDivElement,
        calendlyContainer: query('#calendlyContainer') as HTMLDivElement,
        calendlyCloseButton: query('#calendlyCloseButton') as HTMLButtonElement,
        calendlyWidgetWrapper: query('#calendlyWidgetWrapper') as HTMLDivElement,
        feedbackBar: query('#feedbackBar') as HTMLDivElement,
        feedbackLikeBtn: query('#feedbackLikeBtn') as HTMLButtonElement,
        feedbackDislikeBtn: query('#feedbackDislikeBtn') as HTMLButtonElement,
      };
      this.isInitialized = true;
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description Parse markdown text to HTML
   */
  private parseMarkdownToHTML(text: string): string {
    if (!text || text.trim() === '') {
      console.warn('Empty text passed to parseMarkdownToHTML');
      return '';
    }

    // Handle literal \n from JSON strings
    let normalizedText = text.replace(/\\n/g, '\n');

    // Normalize newlines & collapse excessive blank lines
    const lines = normalizedText
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .split('\n');

    const out: string[] = [];
    let inUL = false;
    let inOL = false;

    const flushLists = () => {
      if (inUL) {
        out.push('</ul>');
        inUL = false;
      }
      if (inOL) {
        out.push('</ol>');
        inOL = false;
      }
    };

    const formatInline = (s: string) => {
      // links [text](url) with URL sanitization
      s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
        const safeUrl = sanitizeURL(url);
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      });
      // **bold**
      s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return s;
    };

    for (let raw of lines) {
      const line = raw.trim();

      // Blank line -> close any open list and add a small spacer
      if (line === '') {
        flushLists();
        continue;
      }

      // Numbered list "1. item"
      const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (olMatch) {
        const item = formatInline(olMatch[2]);
        if (inUL) {
          out.push('</ul>');
          inUL = false;
        }
        if (!inOL) {
          out.push('<ol>');
          inOL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Bulleted list "- item"
      const ulMatch = line.match(/^-\s+(.*)$/);
      if (ulMatch) {
        const item = formatInline(ulMatch[1]);
        if (inOL) {
          out.push('</ol>');
          inOL = false;
        }
        if (!inUL) {
          out.push('<ul>');
          inUL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Standalone label lines ending with ":" (e.g., "Note:", "Tip:", "**Note:**")
      const labelMatch = line.match(/^\**\s*([^*].*?):\s*\**$/);
      if (labelMatch) {
        flushLists();
        const labelText = formatInline(labelMatch[1]);
        out.push(`<strong>${labelText}:</strong><br>`);
        continue;
      }

      // Normal paragraph
      flushLists();
      out.push(`<p>${formatInline(line)}</p>`);
    }

    flushLists();

    // Final tidy-up: remove accidental double breaks or empty paragraphs
    return out
      .join('')
      .replace(/(<br>\s*){2,}/g, '<br>')
      .replace(/<p>\s*<\/p>/g, '');
  }

  /**
   * @description Process and display the default primary message
   */
  private processAndDisplayMessage() {
    if (this.defaultMessageDisplayed) return;
    if (!this.chat?.messagesContainer) {
      console.warn('Messages container not available');
      return;
    }

    if (!this.default_primary_text || this.default_primary_text.trim() === '') {
      console.warn('No default primary text to process');
      return;
    }

    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );

    const formattedContent = this.parseMarkdownToHTML(this.default_primary_text);
    const htmlWrapped = `<div class="md-content">${formattedContent}</div>`;

    const getText = async () => ({
      content: htmlWrapped,
      isHtml: true,
    });
    recieveAndAppendMessage(wrapper, this.chat!.messagesContainer, getText());
    this.defaultMessageDisplayed = true;
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatUrl(url: string) {
    this.url = url;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  set mainWindow(_mainWindow: MainWindow) {
    this._mainWindow = _mainWindow;
  }

  set defaultPrimaryText(text: string) {
    this.default_primary_text = text;

    // If elements aren't ready yet, the text will be processed in connectedCallback
    if (!this.isInitialized || !this.chat?.messagesContainer) {
      return;
    }

    this.processAndDisplayMessage();
  }

  set botBackgroundColor(color: string) {
    this.bot_logo_color = color;
  }

  set sendBackgroundColor(color: string) {
    this.send_button_color = color;
    if (this.chat) this.chat.sendButtonRect.style.fill = color;
  }

  set uniqueId(id: string) {
    this.unique_id = id;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  set apiBaseURL(url: string) {
    this.apiBaseUrl = url;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  set defaultLanguage(language: string) {
    const raw = String(language || '').trim();
    const normalized = raw.toLowerCase().replace(/_/g, '-');
    this.responseLanguage = LANGUAGE_LABELS[normalized] || raw;
  }

  private getResolvedApiBaseUrl() {
    return resolveApiBaseUrl(this.url ?? '', this.apiBaseUrl);
  }

  // --- New customization setters ---

  set bannerText(text: string) {
    if (this.chat?.bannerText) this.chat.bannerText.textContent = text;
  }

  set bannerTextColor(colorValue: string) {
    if (this.chat?.bannerText) this.chat.bannerText.style.color = colorValue;
  }

  set chatHeader(backgroundColor: string) {
    if (this.chat?.chatHeader) this.chat.chatHeader.style.backgroundColor = backgroundColor;
    if (this.chat?.closeButton) this.chat.closeButton.style.backgroundColor = backgroundColor;
    if (this.chat?.backButton)
      (this.chat.backButton as HTMLElement).style.backgroundColor = backgroundColor;
  }

  set logoIcon(iconUrl: string) {
    if (this.chat?.logoIcon) {
      this.chat.logoIcon.src = iconUrl;
      this.chat.logoIcon.style.display = '';
      // Hide default SVG icon
      const defaultIcon = this.chat.chatWindow.querySelector('#defaultChatIcon') as HTMLElement;
      if (defaultIcon) defaultIcon.style.display = 'none';
    }
  }

  set closeButtonColor(colorValue: string) {
    if (this.chat?.closeIcon) (this.chat.closeIcon as HTMLElement).style.fill = colorValue;
  }

  set userChatColor(colorValue: string) {
    this.user_chat_color = colorValue;
  }

  set bannerTextParagraph(text: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.textContent = text;
  }

  set bannerTextParagraphColor(colorValue: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.style.color = colorValue;
  }

  // --- Calendly integration ---

  private async initializeCalendly() {
    if (!this.url || !this.unique_id) return;
    if (this.calendlyInitialized) return;
    // Claim the guard synchronously: the setters and connectedCallback can all
    // call this concurrently, and awaiting before claiming lets them all through.
    this.calendlyInitialized = true;

    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const config = await fetchWidgetConfig(apiBaseUrl, this.unique_id);

      if (config.has_calendly && config.calendly_url) {
        this.calendlyUrl = config.calendly_url;
        this.chat?.calendlyButtonWrap.classList.remove('hidden');
        await loadCalendlyScript();
        this.setupCalendlyEventListener();
      }
      this.handoffEnabled = Boolean(config.text_handoff_enabled);
      if (this.handoffEnabled && this.chat?.handoffButtonWrap) {
        this.chat.handoffButtonWrap.classList.remove('hidden');
      }
      await this.restoreExistingHandoff();
      // Nothing in progress: watch for an agent taking this chat over.
      if (!this.handoffStatus) this.startHandoffWatch();
    } catch (error) {
      // Release the guard so maximizeWidget() can retry a failed init.
      this.calendlyInitialized = false;
      console.error('Failed to initialize Calendly:', error);
    }
  }

  private openCalendly() {
    if (!this.calendlyUrl || !this.chat) return;
    this.setWidgetLayout('calendar');
    this.chat.calendlyContainer.classList.add('active');
    this.chat.calendlyWidgetWrapper.innerHTML = '';
    initCalendlyInline(this.chat.calendlyWidgetWrapper, this.calendlyUrl);
  }

  private closeCalendly() {
    if (!this.chat) return;
    this.chat.calendlyContainer.classList.remove('active');
    this.setWidgetLayout('default');
  }

  private setupCalendlyEventListener() {
    if (this.calendlyListenerAttached) return;
    this.calendlyListenerAttached = true;

    window.addEventListener('message', (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return;
      const data: CalendlyEventData = e.data;
      if (data.event === 'calendly.event_scheduled') {
        this.handleCalendlyEventScheduled(data);
      }
    });
  }

  private async handleCalendlyEventScheduled(data: CalendlyEventData) {
    if (!this.chat || !this.url || !this.unique_id) return;

    // Calendly can emit event_scheduled more than once for a single booking.
    const eventUri = data.payload?.event?.uri || '';
    if (eventUri) {
      if (this.loggedEventUris.has(eventUri)) return;
      this.loggedEventUris.add(eventUri);
    }

    this.closeCalendly();

    const widgetElement = this.chat.chatWindow;
    if (widgetElement) {
      collapseWidget(widgetElement, this.chat.messagesContainer);
      this.setWidgetLayout('default');
      restoreChatMessages(this.chat.messagesContainer);
    }

    const confirmationMessage =
      'Meeting booked successfully! You will receive a confirmation email shortly.';
    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );
    recieveAndAppendMessage(
      wrapper,
      this.chat.messagesContainer,
      Promise.resolve({ content: confirmationMessage })
    );

    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const inviteeUri = data.payload?.invitee?.uri || '';

      await logMeetingBooking(apiBaseUrl, {
        session_id: this.sessionId,
        widget_key: this.unique_id,
        event_uri: eventUri,
        invitee_uri: inviteeUri,
      });
    } catch (error) {
      console.error('Failed to log meeting:', error);
    }
  }

  // --- Feedback ---

  private resetFeedbackTimer() {
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = null;
    }
    // Hide feedback bar when user sends a new message
    this.hideFeedback();
  }

  private startHandoffPresenceTracking() {
    if (!this.handoffStatus) return;
    this.resetHandoffInactivityTimer();

    if (!this.handoffHeartbeatTimer) {
      this.handoffHeartbeatTimer = setInterval(() => {
        if (!this.handoffStatus) return;
        if (
          document.visibilityState === 'hidden' ||
          (typeof navigator !== 'undefined' && navigator.onLine === false)
        )
          return;
        void this.syncHandoffPresence('active', 'heartbeat');
      }, WitzoChatElement.HANDOFF_HEARTBEAT_MS);
    }
  }

  private stopHandoffPresenceTracking() {
    if (this.handoffHeartbeatTimer) {
      clearInterval(this.handoffHeartbeatTimer);
      this.handoffHeartbeatTimer = null;
    }
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    this.handoffMarkedLeft = false;
  }

  private resetHandoffInactivityTimer() {
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    if (!this.handoffStatus) return;
    this.handoffInactiveTimer = setTimeout(() => {
      void this.markHandoffVisitorLeft('inactive_timeout');
    }, WitzoChatElement.HANDOFF_INACTIVE_MS);
  }

  private async markHandoffVisitorActive(reason = 'active', useBeacon = false) {
    if (!this.handoffStatus) return;
    this.handoffMarkedLeft = false;
    this.startHandoffPresenceTracking();
    await this.syncHandoffPresence('active', reason, useBeacon);
    this.startHandoffStream();
    this.scheduleHandoffPolling();
  }

  private async markHandoffVisitorLeft(reason = 'left', useBeacon = false) {
    if (!this.handoffStatus) return;
    this.handoffMarkedLeft = true;
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    this.stopHandoffStream();
    this.stopHandoffPolling();
    await this.syncHandoffPresence('left', reason, useBeacon);
  }

  private stopHandoffPolling() {
    if (this.handoffPollTimer) {
      clearTimeout(this.handoffPollTimer);
      this.handoffPollTimer = null;
    }
  }

  private clearHandoffWaitingIndicator() {
    if (this.handoffWaitingTimer) {
      clearTimeout(this.handoffWaitingTimer);
      this.handoffWaitingTimer = null;
    }
    if (!this.handoffWaitingWrapper) return;
    this.handoffWaitingWrapper.remove();
    this.handoffWaitingWrapper = null;
  }

  private ensureHandoffWaitingIndicator(wrapper?: HTMLDivElement, delayMs = 0) {
    if (!this.chat || !this.handoffAwaitingAgentReply) return;

    const showIndicator = () => {
      if (!this.chat || !this.handoffAwaitingAgentReply) return;
      if (wrapper) {
        if (this.handoffWaitingWrapper && this.handoffWaitingWrapper !== wrapper) {
          this.handoffWaitingWrapper.remove();
        }
        this.handoffWaitingWrapper = wrapper;
      } else if (!this.handoffWaitingWrapper) {
        this.handoffWaitingWrapper = showDots(
          this.chat.messagesContainer,
          this.bot_logo_color ?? '#fc0e3f'
        );
      }

      this.chat.messagesContainer.scrollTop = this.chat.messagesContainer.scrollHeight;
    };

    if (this.handoffWaitingTimer) {
      clearTimeout(this.handoffWaitingTimer);
      this.handoffWaitingTimer = null;
    }

    if (delayMs > 0) {
      this.handoffWaitingTimer = setTimeout(() => {
        this.handoffWaitingTimer = null;
        showIndicator();
      }, delayMs);
      return;
    }

    showIndicator();
  }

  private scheduleHandoffPolling() {
    this.stopHandoffPolling();
    if (!this.handoffEnabled || !this.handoffStatus || !this.unique_id || !this.url) return;
    if (this.handoffEventSource !== null) return;

    this.handoffPollTimer = setTimeout(() => {
      this.pollHandoffStatus().catch((error) => {
        console.warn('[witzo-chat] handoff polling failed:', error);
      });
    }, WitzoChatElement.HANDOFF_POLL_MS);
  }

  private stopHandoffStream() {
    if (!this.handoffEventSource) return;
    this.handoffEventSource.close();
    this.handoffEventSource = null;
  }

  private startHandoffStream() {
    // No handoffStatus guard: the stream also runs in "watch" mode, before any
    // handoff exists, so an agent-initiated takeover reaches the visitor live.
    if (!this.url || !this.unique_id || !this.sessionId || !this.handoffEnabled) return;
    if (typeof EventSource === 'undefined') return;

    this.stopHandoffStream();
    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const streamUrl = `${apiBaseUrl}/chat/handoff/stream?widget_key=${encodeURIComponent(this.unique_id)}&session_id=${encodeURIComponent(this.sessionId)}`;
    const eventSource = new EventSource(streamUrl);
    const refresh = () => {
      // In handoff mode, sync the conversation. Otherwise we are watching for a
      // takeover, so check whether one just opened.
      const task = this.handoffStatus
        ? this.pollHandoffStatus()
        : this.detectHandoffTakeover();
      void task.catch((error) => {
        console.warn('[witzo-chat] handoff stream refresh failed:', error);
      });
    };

    eventSource.addEventListener('message.created', refresh);
    eventSource.addEventListener('handoff.requested', refresh);
    eventSource.addEventListener('handoff.assigned', refresh);
    eventSource.addEventListener('handoff.closed', refresh);
    eventSource.addEventListener('handoff.updated', refresh);
    eventSource.onerror = () => {
      eventSource.close();
      if (this.handoffEventSource === eventSource) {
        this.handoffEventSource = null;
        if (this.handoffStatus) {
          this.scheduleHandoffPolling();
        } else {
          this.scheduleHandoffWatchPoll();
        }
      }
    };

    this.handoffEventSource = eventSource;
  }

  /**
   * Watches a plain AI conversation for an agent taking it over from the
   * dashboard. Without this the visitor would not learn about the takeover until
   * they happened to send another message.
   */
  private startHandoffWatch() {
    if (!this.handoffEnabled || this.handoffStatus) return;
    // Don't hold a connection open for every page view. A visitor who never
    // opened the chat has no server-side session to take over, so there is
    // nothing to watch for until they engage.
    if (!this.hasOpenedWidget) return;
    if (!this.url || !this.unique_id || !this.sessionId) return;
    // Already watching — don't churn the stream on repeated opens.
    if (this.handoffEventSource || this.handoffWatchTimer) return;

    this.startHandoffStream();
    // Only fall back to polling when the stream could not be opened.
    if (!this.handoffEventSource) this.scheduleHandoffWatchPoll();
  }

  private stopHandoffWatch() {
    if (!this.handoffWatchTimer) return;
    clearTimeout(this.handoffWatchTimer);
    this.handoffWatchTimer = null;
  }

  private scheduleHandoffWatchPoll() {
    this.stopHandoffWatch();
    if (!this.handoffEnabled || this.handoffStatus) return;

    this.handoffWatchTimer = setTimeout(() => {
      // No .finally() — the widget targets a pre-ES2018 lib.
      const reschedule = () => this.scheduleHandoffWatchPoll();
      this.detectHandoffTakeover().then(reschedule, (error) => {
        console.warn('[witzo-chat] handoff watch failed:', error);
        reschedule();
      });
    }, WitzoChatElement.HANDOFF_WATCH_POLL_MS);
  }

  private async detectHandoffTakeover() {
    if (!this.url || !this.unique_id || !this.sessionId || !this.chat) return;
    if (!this.handoffEnabled || this.handoffStatus || this.handoffRestoring) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const status = await fetchTextHandoffStatus(
      apiBaseUrl,
      this.unique_id,
      this.sessionId,
      getStoredHandoffId()
    );

    if (!status.active || !status.handoff) return;

    // An agent pulled this chat into a human handoff; switch modes the same way
    // the visitor-initiated flow does.
    this.stopHandoffWatch();
    storeActiveHandoffSession(this.sessionId, status.handoff.id);
    this.handoffStatus = status.handoff.status;
    this.handoffAssignedNotified = status.handoff.status === 'assigned';
    this.setHandoffButtonState(
      status.handoff.status === 'assigned' ? 'assigned' : 'waiting'
    );
    appendHandoffNotification(this.chat.messagesContainer, status.handoff.output);
    await this.markHandoffVisitorActive('agent_takeover');
    await this.pollHandoffStatus();
  }

  private async pollHandoffStatus() {
    if (!this.url || !this.unique_id || !this.chat || !this.sessionId || !this.handoffStatus)
      return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const status = await fetchTextHandoffStatus(
      apiBaseUrl,
      this.unique_id,
      this.sessionId,
      getStoredHandoffId()
    );

    if (!status.active || !status.handoff) {
      const closedMessage = (status as any).closed_message as string | undefined;
      if (closedMessage && this.chat) {
        const wrapper = getPrimaryMessageWrapper(
          this.chat.messagesContainer,
          this.bot_logo_color ?? '#fc0e3f'
        );
        recieveAndAppendMessage(
          wrapper,
          this.chat.messagesContainer,
          Promise.resolve({ content: closedMessage })
        );
      }
      clearHandoffId();
      this.handoffStatus = null;
      this.handoffAssignedNotified = false;
      this.handoffAwaitingAgentReply = false;
      this.clearHandoffWaitingIndicator();
      this.setHandoffButtonState('idle');
      this.stopHandoffPolling();
      this.stopHandoffStream();
      this.stopHandoffPresenceTracking();
      // Back to AI mode — resume watching in case an agent takes over later.
      this.startHandoffWatch();
      return;
    }

    storeActiveHandoffSession(this.sessionId, status.handoff.id);
    const previousStatus = this.handoffStatus;
    this.handoffStatus = status.handoff.status;
    this.startHandoffPresenceTracking();
    this.startHandoffStream();
    this.setHandoffButtonState(status.handoff.status === 'assigned' ? 'assigned' : 'waiting');

    if (
      status.handoff.status === 'assigned' &&
      previousStatus !== 'assigned' &&
      !this.handoffAssignedNotified
    ) {
      this.handoffAssignedNotified = true;
      appendHandoffNotification(this.chat.messagesContainer, status.handoff.output);
    }

    this.handoffAwaitingAgentReply = Boolean(status.awaiting_agent_reply);
    this.clearHandoffWaitingIndicator();
    // Handoff typing indicator disabled: when a human agent is connected,
    // don't show the "..." waiting bubble while the visitor is chatting.
    // if (!this.handoffAwaitingAgentReply) {
    //   this.clearHandoffWaitingIndicator();
    // } else {
    //   this.ensureHandoffWaitingIndicator(
    //     undefined,
    //     Math.max(0, Number(status.wait_remaining_ms || 0))
    //   );
    // }

    const newMessages = status.messages.filter(
      (message) => !this.seenHandoffMessageIds.has(message.id)
    );

    for (const message of newMessages) {
      this.seenHandoffMessageIds.add(message.id);

      const wrapper = getPrimaryMessageWrapper(
        this.chat.messagesContainer,
        this.bot_logo_color ?? '#fc0e3f'
      );
      recieveAndAppendMessage(
        wrapper,
        this.chat.messagesContainer,
        Promise.resolve({ content: message.content })
      );
    }

    this.scheduleHandoffPolling();
  }

  private async restoreExistingHandoff() {
    if (!this.url || !this.unique_id || !this.sessionId || !this.chat || !this.handoffEnabled)
      return;

    this.handoffRestoring = true;
    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const storedHandoffId = getStoredHandoffId();
      const status = await fetchTextHandoffStatus(
        apiBaseUrl,
        this.unique_id,
        this.sessionId,
        storedHandoffId
      );
      if (!status.active || !status.handoff) {
        clearHandoffId();
        return;
      }

      storeActiveHandoffSession(this.sessionId, status.handoff.id);
      this.handoffStatus = status.handoff.status;
      this.handoffAssignedNotified = status.handoff.status === 'assigned';
      this.setHandoffButtonState(status.handoff.status === 'assigned' ? 'assigned' : 'waiting');
      await this.markHandoffVisitorActive('restored');
      await this.pollHandoffStatus();
    } catch (error) {
      console.warn('[witzo-chat] Failed to restore handoff:', error);
    } finally {
      this.handoffRestoring = false;
    }
  }

  private removeHandoffLeadForm() {
    if (!this.handoffLeadFormWrapper) return;
    this.handoffLeadFormWrapper.remove();
    this.handoffLeadFormWrapper = null;
  }

  private isBusinessEmail(email: string) {
    const normalized = email.trim().toLowerCase();
    const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
    if (!isValidFormat) return false;
    const domain = normalized.split('@').pop() || '';
    return Boolean(domain) && !FREE_EMAIL_DOMAINS.has(domain);
  }

  private showHandoffLeadForm() {
    if (!this.chat) return;
    if (this.handoffLeadFormWrapper) {
      this.handoffLeadFormWrapper.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }

    const card = document.createElement('div');
    card.className = 'handoff-lead-card';
    card.innerHTML = `
      <p class="handoff-lead-title">Connect with a human</p>
      <p class="handoff-lead-copy">Share your details so our team can identify your account.</p>
      <form class="handoff-lead-form" novalidate>
        <label class="handoff-lead-field">
          <span class="handoff-lead-label">Name</span>
          <input class="handoff-lead-input" name="name" autocomplete="name" maxlength="191" required />
        </label>
        <label class="handoff-lead-field">
          <span class="handoff-lead-label">Country</span>
          <input class="handoff-lead-input" name="country" autocomplete="country-name" maxlength="191" required />
        </label>
        <label class="handoff-lead-field">
          <span class="handoff-lead-label">Business email</span>
          <input class="handoff-lead-input" name="email" type="email" autocomplete="email" maxlength="191" required />
        </label>
        <div class="handoff-lead-error" aria-live="polite"></div>
        <div class="handoff-lead-actions">
          <button class="handoff-lead-cancel" type="button">Cancel</button>
          <button class="handoff-lead-submit" type="submit">Connect</button>
        </div>
      </form>
    `;

    const form = card.querySelector('form') as HTMLFormElement;
    const error = card.querySelector('.handoff-lead-error') as HTMLDivElement;
    const submitButton = card.querySelector('.handoff-lead-submit') as HTMLButtonElement;
    const cancelButton = card.querySelector('.handoff-lead-cancel') as HTMLButtonElement;

    cancelButton.addEventListener('click', () => {
      this.removeHandoffLeadForm();
      this.setHandoffButtonState('idle');
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const lead: HandoffLeadDetails = {
        name: String(formData.get('name') || '').trim(),
        country: String(formData.get('country') || '').trim(),
        email: String(formData.get('email') || '')
          .trim()
          .toLowerCase(),
      };

      if (lead.name.length < 2) {
        error.textContent = 'Please enter your name.';
        return;
      }
      if (lead.country.length < 2) {
        error.textContent = 'Please enter your country.';
        return;
      }
      if (!this.isBusinessEmail(lead.email)) {
        error.textContent = 'Please use your business email address.';
        return;
      }

      error.textContent = '';
      submitButton.disabled = true;
      submitButton.textContent = 'Connecting...';
      void this.submitHumanHandoffRequest(lead).catch((submitError) => {
        submitButton.disabled = false;
        submitButton.textContent = 'Connect';
        error.textContent =
          submitError instanceof Error
            ? submitError.message
            : 'Unable to connect you to a human agent right now.';
      });
    });

    this.handoffLeadFormWrapper = card;
    this.chat.messagesContainer.appendChild(card);
    this.chat.messagesContainer.scrollTop = this.chat.messagesContainer.scrollHeight;
  }

  private async submitHumanHandoffRequest(lead: HandoffLeadDetails) {
    if (!this.url || !this.unique_id || !this.chat || !this.handoffEnabled) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    this.handoffAssignedNotified = false;
    this.setHandoffButtonState('requesting');

    const response = await requestTextHandoff(
      apiBaseUrl,
      this.unique_id,
      this.sessionId,
      'visitor_requested_human',
      lead
    );
    this.removeHandoffLeadForm();
    this.handoffStatus = response.handoff?.status ?? 'waiting_for_agent';
    if (response.handoff?.id) storeActiveHandoffSession(this.sessionId, response.handoff.id);
    appendHandoffNotification(this.chat.messagesContainer, response.content);
    await this.markHandoffVisitorActive('handoff_requested');
    this.setHandoffButtonState('waiting');
    await this.pollHandoffStatus();
  }

  private async requestHumanHandoff() {
    if (!this.url || !this.unique_id || !this.chat || !this.handoffEnabled) return;

    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      if (this.handoffStatus) {
        await this.disconnectHumanHandoff();
        return;
      }

      const cooldownRemainingMs = this.handoffReconnectAvailableAt - Date.now();
      if (cooldownRemainingMs > 0) {
        appendHandoffNotification(
          this.chat.messagesContainer,
          `Please wait ${Math.ceil(cooldownRemainingMs / 1000)} seconds before starting a new human chat.`
        );
        return;
      }

      this.handoffAssignedNotified = false;
      this.showHandoffLeadForm();
    } catch (error) {
      console.warn('[witzo-chat] Failed to request human handoff:', error);
      this.setHandoffButtonState('idle');
      appendHandoffNotification(
        this.chat.messagesContainer,
        error instanceof Error ? error.message : 'Unable to connect you to a human agent right now.'
      );
    }
  }

  private async disconnectHumanHandoff() {
    if (!this.url || !this.unique_id || !this.chat || !this.handoffStatus) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const previousStatus = this.handoffStatus;
    this.setHandoffButtonState('requesting');

    try {
      await closeTextHandoff(apiBaseUrl, this.unique_id, this.sessionId, getStoredHandoffId());
      clearHandoffId();
      this.handoffStatus = null;
      this.handoffReconnectAvailableAt =
        Date.now() + WitzoChatElement.HANDOFF_RECONNECT_COOLDOWN_MS;
      this.handoffAssignedNotified = false;
      this.seenHandoffMessageIds.clear();
      this.handoffAwaitingAgentReply = false;
      this.clearHandoffWaitingIndicator();
      this.stopHandoffPolling();
      this.stopHandoffStream();
      this.stopHandoffPresenceTracking();
      this.setHandoffButtonState('idle');
      appendHandoffNotification(
        this.chat.messagesContainer,
        'Thank you for chatting with us. Feel free to ask anything.'
      );
    } catch (error) {
      console.warn('[witzo-chat] Failed to disconnect human handoff:', error);
      this.handoffStatus = previousStatus;
      this.setHandoffButtonState(previousStatus === 'assigned' ? 'assigned' : 'waiting');
      appendHandoffNotification(
        this.chat.messagesContainer,
        error instanceof Error
          ? error.message
          : 'Unable to disconnect from the human agent right now.'
      );
    }
  }

  private setHandoffButtonState(state: 'idle' | 'requesting' | 'waiting' | 'assigned') {
    if (!this.chat?.handoffButton || !this.chat?.handoffButtonWrap) return;

    const tooltip = this.chat.handoffButtonWrap.querySelector('.handoff-tooltip');
    const tooltipText =
      state === 'assigned'
        ? 'Disconnect human agent'
        : state === 'waiting' || state === 'requesting'
          ? state === 'requesting'
            ? 'Connecting to human'
            : 'Cancel human handoff'
          : 'Talk to human';

    this.chat.handoffButton.disabled = state === 'requesting';
    this.chat.handoffButton.setAttribute('data-state', state);
    this.chat.handoffButton.setAttribute('aria-label', tooltipText);
    if (tooltip) tooltip.textContent = tooltipText;
  }

  private async syncHandoffPresence(state: 'active' | 'left', reason = '', useBeacon = false) {
    if (!this.url || !this.unique_id || !this.sessionId || !this.handoffStatus) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    try {
      await notifyTextHandoffPresence(
        apiBaseUrl,
        this.unique_id,
        this.sessionId,
        state,
        reason,
        useBeacon
      );
    } catch (error) {
      console.warn('[witzo-chat] Failed to sync handoff presence:', error);
    }
  }

  private startFeedbackTimer() {
    if (this.feedbackSubmitted) return;
    this.resetFeedbackTimer();
    this.feedbackTimer = setTimeout(() => {
      this.showFeedback();
    }, WitzoChatElement.FEEDBACK_IDLE_MS);
  }

  private showFeedback() {
    if (!this.chat || this.feedbackSubmitted) return;
    this.chat.feedbackBar.classList.remove('hidden');
  }

  private hideFeedback() {
    if (!this.chat) return;
    this.chat.feedbackBar.classList.add('hidden');
  }

  private async submitFeedback(type: 'like' | 'dislike') {
    if (!this.chat || !this.url || !this.unique_id || this.feedbackSubmitted) return;

    this.feedbackSubmitted = true;

    // Visual feedback: highlight selected button
    if (type === 'like') {
      this.chat.feedbackLikeBtn.classList.add('selected');
    } else {
      this.chat.feedbackDislikeBtn.classList.add('selected');
    }

    // Replace with thank you message after a brief moment
    setTimeout(() => {
      if (!this.chat) return;
      this.chat.feedbackBar.innerHTML =
        '<p class="feedback-thanks">Thank you for your feedback!</p>';
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.hideFeedback();
      }, 3000);
    }, 500);

    // Call backend API
    try {
      const apiBaseUrl = new URL(this.url).origin + '/api/v1';
      await fetch(`${apiBaseUrl}/sessions/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widget_key: this.unique_id,
          session_id: this.sessionId,
          feedback: type,
        }),
      });
    } catch (err) {
      console.warn('[witzo-chat] Failed to submit feedback:', err);
    }
  }

  private detectConversationEndIntent(text: string): boolean {
    const normalized = text
      .trim()
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/[^a-z0-9'\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) return false;

    const endPhrases = [
      'no thanks',
      'i am done',
      "i'm done",
      "that's all",
      'bye',
      'thank you',
      'thanks',
      'okay thanks',
      'ok thanks',
      'no thank you',
    ];

    return endPhrases.some((phrase) => normalized.includes(phrase));
  }

  private detectHumanHandoffIntent(text: string): boolean {
    const normalized = text
      .trim()
      .toLowerCase()
      .replace(/[â€™']/g, "'")
      .replace(/[^a-z0-9'\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) return false;

    const handoffPhrases = [
      'talk to human',
      'talk with human',
      'speak to human',
      'speak with human',
      'connect me to human',
      'connect with human',
      'human agent',
      'live agent',
      'real agent',
      'support agent',
      'customer support',
      'human support',
      'talk to person',
      'speak to person',
      'representative',
    ];

    return handoffPhrases.some((phrase) => normalized.includes(phrase));
  }

  private showHumanHandoffUnavailable() {
    if (!this.chat) return;
    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );
    recieveAndAppendMessage(
      wrapper,
      this.chat.messagesContainer,
      Promise.resolve({
        content: 'Right now no human agent is available. Please try again later.',
      })
    );
  }

  // --- Lifecycle ---

  async connectedCallback() {
    this.bindEvents();
    this.sessionId = createToken();
    await this.initializeCalendly();

    // Process any pending default text after connection and initialization
    if (this.isInitialized && this.default_primary_text) {
      this.processAndDisplayMessage();
    }

    window.addEventListener('pagehide', this.handoffPresenceHandler);
    window.addEventListener('beforeunload', this.handoffPresenceHandler);
    document.addEventListener('visibilitychange', this.handoffVisibilityHandler);
    window.addEventListener('online', this.handoffOnlineHandler);
    window.addEventListener('offline', this.handoffOfflineHandler);
    window.addEventListener('storage', this.activeHandoffSessionHandler);
    window.addEventListener(
      'witzo-active-handoff-session-change',
      this.activeHandoffSessionHandler
    );
    ['click', 'keydown', 'mousemove', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, this.handoffActivityHandler, { passive: true });
    });
  }

  disconnectedCallback() {
    void this.markHandoffVisitorLeft('component_disconnected', true);
    window.removeEventListener('pagehide', this.handoffPresenceHandler);
    window.removeEventListener('beforeunload', this.handoffPresenceHandler);
    document.removeEventListener('visibilitychange', this.handoffVisibilityHandler);
    window.removeEventListener('online', this.handoffOnlineHandler);
    window.removeEventListener('offline', this.handoffOfflineHandler);
    window.removeEventListener('storage', this.activeHandoffSessionHandler);
    window.removeEventListener(
      'witzo-active-handoff-session-change',
      this.activeHandoffSessionHandler
    );
    ['click', 'keydown', 'mousemove', 'touchstart'].forEach((eventName) => {
      window.removeEventListener(eventName, this.handoffActivityHandler);
    });
    this.stopHandoffPolling();
    this.stopHandoffWatch();
    this.stopHandoffStream();
    this.stopHandoffPresenceTracking();
    this.handoffAwaitingAgentReply = false;
    this.clearHandoffWaitingIndicator();
    this.resetFeedbackTimer();
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const {
      backButton,
      closeButton,
      sendButton,
      messageInput,
      messagesContainer,
      chatWindow,
      calendlyButton,
      calendlyCloseButton,
      handoffButton,
    } = this.chat;

    backButton.addEventListener('click', () => {
      this._mainWindow.back();
    });

    closeButton.addEventListener('click', () => {
      void this.markHandoffVisitorLeft('widget_closed');
      this._mainWindow.close();
    });

    // Calendly button handlers
    calendlyButton.addEventListener('click', () => {
      this.openCalendly();
    });
    calendlyCloseButton.addEventListener('click', () => {
      this.closeCalendly();
    });
    handoffButton.addEventListener('click', () => {
      this.requestHumanHandoff();
    });

    // Feedback button handlers
    this.chat.feedbackLikeBtn.addEventListener('click', () => {
      this.submitFeedback('like');
    });
    this.chat.feedbackDislikeBtn.addEventListener('click', () => {
      this.submitFeedback('dislike');
    });

    const gapRequired = 2 * 60 * 1000;
    sendButton.addEventListener('click', () => {
      if (!this.url) return;
      const text = this.chat!.messageInput.value.trim();
      const shouldPromptFeedbackAfterReply = this.detectConversationEndIntent(text);
      const shouldRequestHumanHandoff = this.detectHumanHandoffIntent(text);
      const requestId = ++this.latestRequestId;

      const gap = new Date().getTime() - this.date.getTime();
      if (gap > gapRequired && !this.handoffStatus && !this.handoffRestoring) {
        this.succesfulChatCount = 0;
        this.date = new Date();
        localStorage.setItem('witzo_chat_date', this.date.toISOString());
      }

      if (text !== '') {
        if (this.handoffStatus) {
          void this.markHandoffVisitorActive('user_message');
        }
        // Hide feedback bar when user sends a new message
        this.resetFeedbackTimer();

        sendAndAppendMessage(
          messagesContainer,
          messageInput,
          text,
          this.user_chat_color ?? '#FFDDE4'
        );

        if (!this.handoffStatus && shouldRequestHumanHandoff) {
          if (this.handoffEnabled) {
            void this.requestHumanHandoff();
          } else {
            this.showHumanHandoffUnavailable();
          }
          return;
        }

        // Check booking intent
        if (detectBookingIntent(text) && this.calendlyUrl && this.chat) {
          showCalendlyLoadingMessage(messagesContainer);
          setTimeout(() => {
            const widgetElement = this.chat!.chatWindow;
            const handleSkip = () => {
              collapseWidget(widgetElement, messagesContainer);
              this.setWidgetLayout('default');
              restoreChatMessages(messagesContainer);
            };
            this.setWidgetLayout('calendar');
            appendInlineCalendlyWidget(
              messagesContainer,
              this.calendlyUrl!,
              widgetElement,
              handleSkip
            );
          }, 500);
          return;
        }

        // During human handoff, don't show the AI/loading "..." bubble after a visitor sends a message.
        const wrapper = this.handoffStatus
          ? null
          : showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
        let responsePromise = fetchChatResponse(
          this.url,
          this.sessionId,
          text,
          this.unique_id ?? '',
          this.succesfulChatCount,
          this.responseLanguage
        );

        this.succesfulChatCount += 1;
        sessionStorage.setItem('witzo_chat_count', `${this.succesfulChatCount}`);

        responsePromise.then(async (response) => {
          if (
            !this.handoffStatus &&
            !this.handoffRestoring &&
            response.content?.includes('You have exceeded your Chat limit')
          ) {
            this.sessionId = resetToken();
            response = await fetchChatResponse(
              this.url ?? '',
              this.sessionId,
              text,
              this.unique_id ?? '',
              this.succesfulChatCount,
              this.responseLanguage
            );
          }

          if (requestId !== this.latestRequestId) {
            wrapper?.remove();
            return;
          }
          if (response.handoff?.active) {
            this.handoffStatus = response.handoff.status;
            wrapper?.remove();
            this.setHandoffButtonState(
              response.handoff.status === 'assigned' ? 'assigned' : 'waiting'
            );
            await this.pollHandoffStatus();
          } else {
            this.handoffAwaitingAgentReply = false;
            this.clearHandoffWaitingIndicator();
            const responseWrapper =
              wrapper ?? showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
            await recieveAndAppendMessage(
              responseWrapper,
              messagesContainer,
              Promise.resolve(response)
            );
          }

          if (shouldPromptFeedbackAfterReply) {
            this.showFeedback();
          } else {
            // Start idle timer after AI response is displayed
            this.startFeedbackTimer();
          }
        });
      }
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendButton.click();
      }
    });
  }

  /**Minimises the chat window */
  minimizeWidget() {
    if (!this.chat) return;

    this.closeCalendly();
    this.chat.chatWindow.classList.add('minimizing');
    setTimeout(() => {
      this.setWidgetLayout('default');
      this.chat?.chatWindow.classList.add('hidden');
      this.chat?.chatWindow.classList.remove('minimizing');
    }, 300);
  }

  private setWidgetLayout(mode: 'default' | 'chat' | 'calendar') {
    if (!this.chat) return;

    this.chat.chatWindow.classList.toggle('chat-expanded', mode === 'chat');
    this.chat.chatWindow.classList.toggle('calendar-expanded', mode === 'calendar');

    this.dispatchEvent(
      new CustomEvent('witzo-widget-layout-change', {
        detail: { mode },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**Maximize the chat window */
  maximizeWidget() {
    if (!this.chat) return;

    this.chat.chatWindow.classList.remove('hidden');
    this.setWidgetLayout('default');
    this.chat.messageInput.focus();
    this.hasOpenedWidget = true;
    if (this.handoffStatus) {
      void this.markHandoffVisitorActive('widget_opened');
    } else {
      // Watch for an agent taking this chat over from the dashboard.
      this.startHandoffWatch();
    }

    // Retry Calendly initialization if it wasn't done yet (props may not have been set during connectedCallback)
    if (!this.calendlyInitialized && this.url && this.unique_id) {
      this.initializeCalendly();
    }
  }
}

export { WitzoChatElement };
